import { DEFAULT_FILTERS, type LensFilters, VIEWS, type ViewId } from './liveDataAdapter';

// The worksheet, dates and split settings live in the URL next to the quick filters, so a copied
// link opens the same numbers. Only values that differ from the defaults are written, which keeps
// the rolling default end date (yesterday in Sydney) rolling for links that never changed it.
const VIEW_KEY = 'avis_view';
const FILTER_PREFIX = 'avis_';

export const DATE_KEYS = ['cp_start', 'cp_end', 'pp_start', 'pp_end'] as const;
export type DateKey = (typeof DATE_KEYS)[number];

export const SPLITS = {
  conv_split1: [
    'All',
    'Brand BK',
    'Category Name',
    'Product Name',
    'SPP Property Name',
    'Deal Tier',
    'Property Country',
    'Property City',
    'Website Region',
    'Property Chain Name',
    'Product Page Type',
    'Traffic Channel',
    'Lead Time',
    'Vendor Name',
    'Chains',
  ],
  conv_split2: [
    'All',
    'Brand BK',
    'Category Name',
    'Product Name',
    'SPP Property Name',
    'Deal Tier',
    'Property Country',
    'Property City',
    'Website Region',
    'Property Chain Name',
    'Product Page Type',
    'Traffic Channel',
    'Lead Time',
  ],
  sup_split1: [
    'Supplier',
    'Supplier Type',
    'All',
    'Channel',
    'Board Code',
    'Refund Flag',
    'Rate Plan',
    'Property Name',
    'Property Country',
    'Property State',
    'Property City',
    'Property Chain',
    'Channel Manager',
    'Brand',
    'Website Region',
  ],
  sup_split2: [
    'All',
    'Supplier',
    'Supplier Type',
    'Channel',
    'Board Code',
    'Refund Flag',
    'Rate Plan',
    'Property Name',
    'Property Country',
    'Property State',
    'Property City',
    'Property Chain',
    'Channel Manager',
    'Brand',
    'Website Region',
  ],
  lpc_split1: [
    'All',
    'Vendor Name',
    'Offer Country Name',
    'Page Type',
    'Property ID',
    'Campaign Name',
    'Concurrent Flash Deal On',
    'LPC Package Rates Available',
    'Tactical Offer On (SF)',
    'Tactical Campaign Flag',
    'Tactical Campaign Sources',
    'Has Had a Tactical Campaign',
  ],
  lpc_split2: [
    'All',
    'Vendor Name',
    'Offer Country Name',
    'Page Type',
    'Property ID',
    'Campaign Name',
    'Tactical Campaign Sources',
    'Tactical Campaign Flag',
    'Has Had a Tactical Campaign',
  ],
} as const;

export const CHOICES = {
  comparison: ['Custom', 'Last Year'],
  conv_scope: ['CP only', 'CP and PP'],
  margin_max: ['Exclude', 'Include'],
  rate_position: ['All', 'Lead Room', 'Lead Rate', 'Non-Lead Rate'],
  ...SPLITS,
} as const;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar date written as YYYY-MM-DD. */
export function isIsoDate(value: string): boolean {
  if (!ISO_DATE.test(value)) return false;
  const parsed = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}

export function readView(params: URLSearchParams): ViewId {
  const value = params.get(VIEW_KEY);
  return VIEWS.includes(value as ViewId) ? (value as ViewId) : VIEWS[0];
}

export function writeView(view: ViewId, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  if (view === VIEWS[0]) next.delete(VIEW_KEY);
  else next.set(VIEW_KEY, view);
  return next;
}

/** Reads the dates and settings from a link, ignoring anything that is not a valid value. */
export function readFilters(params: URLSearchParams): LensFilters {
  const read = (key: keyof LensFilters): string => {
    const value = params.get(`${FILTER_PREFIX}${key}`);
    if (value === null) return DEFAULT_FILTERS[key];
    if ((DATE_KEYS as ReadonlyArray<string>).includes(key)) return isIsoDate(value) ? value : DEFAULT_FILTERS[key];
    const allowed = (CHOICES as Record<string, ReadonlyArray<string>>)[key];
    return allowed?.includes(value) ? value : DEFAULT_FILTERS[key];
  };
  return Object.fromEntries(
    (Object.keys(DEFAULT_FILTERS) as Array<keyof LensFilters>).map((key) => [key, read(key)]),
  ) as LensFilters;
}

export function writeFilters(filters: LensFilters, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  (Object.keys(DEFAULT_FILTERS) as Array<keyof LensFilters>).forEach((key) => {
    const name = `${FILTER_PREFIX}${key}`;
    if (filters[key] === DEFAULT_FILTERS[key]) next.delete(name);
    else next.set(name, filters[key]);
  });
  return next;
}

/** Removes every date and setting from the link, leaving the quick filters and other keys. */
export function clearFilters(current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  (Object.keys(DEFAULT_FILTERS) as Array<keyof LensFilters>).forEach((key) => next.delete(`${FILTER_PREFIX}${key}`));
  return next;
}

const DATE_LABELS: Record<DateKey, string> = {
  cp_start: 'Reporting start',
  cp_end: 'Reporting end',
  pp_start: 'Previous start',
  pp_end: 'Previous end',
};

/**
 * Checks a typed date before it is sent to the warehouse. Returns the message to show under the
 * field, or null when the date can be used.
 */
export function dateProblem(key: DateKey, value: string, filters: LensFilters, today: string): string | null {
  if (!isIsoDate(value)) return 'Enter a full date (day, month and four-digit year).';
  if (value > today) return `${DATE_LABELS[key]} cannot be after today.`;
  const pair: Record<DateKey, { other: DateKey; startSide: boolean }> = {
    cp_start: { other: 'cp_end', startSide: true },
    cp_end: { other: 'cp_start', startSide: false },
    pp_start: { other: 'pp_end', startSide: true },
    pp_end: { other: 'pp_start', startSide: false },
  };
  const { other, startSide } = pair[key];
  const otherValue = filters[other];
  if (isIsoDate(otherValue)) {
    if (startSide && value > otherValue) return `${DATE_LABELS[key]} must be on or before ${DATE_LABELS[other]}.`;
    if (!startSide && value < otherValue) return `${DATE_LABELS[key]} must be on or after ${DATE_LABELS[other]}.`;
  }
  return null;
}
